import { FilterConfig, FilterOption } from '@/component-config/DynamicFilterConfig';
import {
  formatLocalYmd,
  matchNumberPresetFromParams,
  matchRelativeDatePresetFromParams,
  numberRangePresetLabel,
  relativeDatePresetLabel,
  resolveDateRangeBounds,
  resolveNumberRangeBounds,
} from '@/lib/filters/rangePresets';

export interface FilterQueryParams {
  [key: string]: string | string[];
}

export interface FilterServiceOptions {
  apiEndpoint?: string;
  entityType?: string;
  pageSize?: number;
  defaultParams?: FilterQueryParams;
  searchFields?: string;
}

/**
 * Service for managing dynamic filters and generating query parameters
 * compatible with the backend Django ORM filtering system
 */
// Encapsulates translation of UI filter state into backend query params (Django ORM-style)
export class FilterService {
  private filters: FilterConfig[] = [];
  private options: FilterServiceOptions;

  constructor(filters: FilterConfig[] = [], options: FilterServiceOptions = {}) {
    this.filters = filters;
    this.options = {
      pageSize: 10,
      ...options
    };
  }

  /**
   * Update the filter configuration
   */
  setFilters(filters: FilterConfig[]): void {
    this.filters = filters;
  }

  /**
   * Update service options
   */
  setOptions(options: FilterServiceOptions): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Get filter configuration by key
   */
  getFilter(key: string): FilterConfig | undefined {
    return this.filters.find(filter => filter.key === key);
  }

  /**
   * Get all filter options for a select filter
   */
  getFilterOptions(key: string): FilterOption[] {
    const filter = this.getFilter(key);
    return filter?.options || [];
  }

  /**
   * Validate filter configuration for common issues
   */
  validateFilters(): { isValid: boolean; errors: string[]; warnings: string[] } {
    const errors: string[] = [];
    const warnings: string[] = [];
    const paramNames = new Set<string>();
    const keys = new Set<string>();

    this.filters.forEach((filter, index) => {
      const accessor = filter.accessor || filter.key;

      if (!accessor || accessor.trim() === '') {
        errors.push(`Filter ${index + 1} (${filter.label || filter.key}): Missing accessor field`);
      } else {
        const lookup = filter.lookup || this.getLookupFromType(filter.type);
        const effectiveParam = this.getEffectiveParamName(accessor, filter.type, lookup);
        const paramList = Array.isArray(effectiveParam) ? effectiveParam : [effectiveParam];
        for (const p of paramList) {
          if (paramNames.has(p)) {
            errors.push(`Filter ${index + 1} (${filter.label || filter.key}): Duplicate query param "${p}" - this will cause the same values to be sent to multiple fields`);
          } else {
            paramNames.add(p);
          }
        }
      }

      if (!filter.key || filter.key.trim() === '') {
        errors.push(`Filter ${index + 1} (${filter.label || filter.key}): Missing key field`);
      } else if (keys.has(filter.key)) {
        errors.push(`Filter ${index + 1} (${filter.label || filter.key}): Duplicate key "${filter.key}"`);
      } else {
        keys.add(filter.key);
      }

      if (!filter.label || filter.label.trim() === '') {
        warnings.push(`Filter ${index + 1}: Missing label field`);
      }

      if (filter.type === 'select') {
        const hasApiOptions = !!(filter.optionsApiUrl && filter.optionsApiUrl.trim());
        if (hasApiOptions) {
          if (!filter.optionsDisplayKey?.trim()) {
            errors.push(`Filter ${index + 1} (${filter.label || filter.key}): optionsDisplayKey is required when using API`);
          }
          if (!filter.optionsValueKey?.trim()) {
            errors.push(`Filter ${index + 1} (${filter.label || filter.key}): optionsValueKey is required when using API`);
          }
        } else if (!filter.options || filter.options.length === 0) {
          errors.push(`Filter ${index + 1} (${filter.label || filter.key}): Select filters must have options or optionsApiUrl`);
        }
      }

      // Check if accessor and key are the same (which is good for consistency)
      if (filter.accessor && filter.key && filter.accessor !== filter.key) {
        warnings.push(`Filter ${index + 1} (${filter.label}): Accessor "${filter.accessor}" and key "${filter.key}" are different - this might cause confusion`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Generate query parameters from filter values
   */
  // Build URLSearchParams including default params and per-filter lookups
  generateQueryParams(filterValues: Record<string, any>): URLSearchParams {
    const params = new URLSearchParams();

    // Add default parameters
    if (this.options.entityType) {
      params.append('entity_type', this.options.entityType);
    }

    if (this.options.defaultParams) {
      Object.entries(this.options.defaultParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(key, v));
        } else {
          params.append(key, String(value));
        }
      });
    }

    // Process each configured filter
    this.filters.forEach(filter => {
      const value = filterValues[filter.key];
      if (this.isEmpty(value)) return;

      const accessor = filter.accessor || filter.key;

      // Skip filters with empty accessor or key to prevent duplicate field mapping
      if (!accessor || accessor.trim() === '') {
        return;
      }

      this.addFilterParam(params, accessor, filter, value);
    });

    // Handle global search if present in filterValues but not as a configured filter
    if (
      filterValues.search &&
      !this.filters.some(
        (f) => f.key === 'search' || (f.type === 'search' && !this.isEmpty(filterValues[f.key]))
      )
    ) {
        this.addSearchParam(params, filterValues.search);
    }
    
    // Note: Pagination is handled separately by the component, not by the filter service
    // The filter service should only handle filter parameters

    return params;
  }

  /**
   * Add a single filter parameter to URLSearchParams
   */
  // Route a filter/value to the appropriate param builder based on its type
  private addFilterParam(
    params: URLSearchParams,
    accessor: string,
    filter: FilterConfig,
    value: any
  ): void {
    // Use custom lookup if provided, otherwise determine from filter type
    const lookup = filter.lookup || this.getLookupFromType(filter.type);

    switch (filter.type) {
      case 'select':
        this.addSelectParam(params, accessor, value, lookup);
        break;
      case 'text':
        this.addTextParam(params, accessor, value, lookup);
        break;
      case 'search':
        this.addSearchParam(params, value);
        break;
      case 'date_gte':
      case 'date_lte':
      case 'date_exact':
      case 'date_range':
      case 'date_time_range':
        this.addDateParam(params, accessor, filter, value);
        break;
      case 'number_gte':
      case 'number_lte':
      case 'number_range':
      case 'gt':
      case 'lt':
        this.addNumberParam(params, accessor, filter, value);
        break;
      case 'exact':
      case 'icontains':
      case 'startswith':
      case 'endswith':
      case 'in':
        this.addGenericParam(params, accessor, filter, value);
        break;
      default:
        this.addDefaultParam(params, accessor, value, lookup);
    }
  }

  /**
   * Get Django ORM lookup from filter type
   */
  private getEffectiveParamName(accessor: string, type: FilterConfig['type'], lookup: string): string | string[] {
    switch (type) {
      case 'select':
      case 'in':
        return (lookup === 'in' || lookup === '') ? accessor : `${accessor}__${lookup}`;
      case 'text':
      case 'icontains':
        return `${accessor}__icontains`;
      case 'search':
        return 'search';
      case 'date_range':
      case 'date_time_range':
      case 'number_range':
        return [`${accessor}__gte`, `${accessor}__lte`];
      case 'date_gte':
      case 'number_gte':
        return `${accessor}__gte`;
      case 'date_lte':
      case 'number_lte':
        return `${accessor}__lte`;
      case 'date_exact':
        return accessor;
      case 'gt':
        return `${accessor}__gt`;
      case 'lt':
        return `${accessor}__lt`;
      case 'exact':
        return `${accessor}__exact`;
      case 'startswith':
        return `${accessor}__startswith`;
      case 'endswith':
        return `${accessor}__endswith`;
      default:
        return lookup ? `${accessor}__${lookup}` : `${accessor}__icontains`;
    }
  }

  private getLookupFromType(type: FilterConfig['type']): string {
    switch (type) {
      case 'select':
        return 'in'; // Multiple selections use IN lookup
      case 'text':
        return 'icontains';
      case 'exact':
        return 'exact';
      case 'icontains':
        return 'icontains';
      case 'startswith':
        return 'startswith';
      case 'endswith':
        return 'endswith';
      case 'search':
        return 'search';
      case 'date_gte':
        return 'gte';
      case 'date_lte':
        return 'lte';
      case 'date_exact':
        return '';
      case 'date_range':
      case 'date_time_range':
        return 'range';
      case 'number_gte':
        return 'gte';
      case 'number_lte':
        return 'lte';
      case 'number_range':
        return 'range';
      case 'gt':
        return 'gt';
      case 'lt':
        return 'lt';
      case 'in':
        return 'in';
      default:
        return 'icontains';
    }
  }

  /**
   * Add select filter parameter (supports multiple values)
   */
  // Append select values; arrays use multiple parameters for proper backend handling
  private addSelectParam(params: URLSearchParams, accessor: string, value: any, lookup: string = 'in'): void {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        // For IN lookup, join with commas, as requested by user
        if (lookup === 'in' || lookup === '') {
          params.append(accessor, value.join(','));
        } else {
          // For other lookups, add multiple parameters
          value.forEach(v => params.append(`${accessor}__${lookup}`, v));
        }
      }
    } else if (typeof value === 'string' && value.trim() !== '') {
      if (lookup === 'in' || lookup === '') {
        params.append(accessor, value);
      } else {
        params.append(`${accessor}__${lookup}`, value);
      }
    }
  }

  /**
   * Add text filter parameter with specified lookup
   */
  // Append text value with the chosen lookup (icontains by default)
  private addTextParam(params: URLSearchParams, accessor: string, value: string, lookup: string = 'icontains'): void {
    if (typeof value === 'string' && value.trim() !== '') {
      const paramName = lookup === 'icontains' || lookup === '' ? `${accessor}__icontains` : `${accessor}__${lookup}`;
      params.append(paramName, value.trim());
    }
  }

  /**
   * Add global search parameter
   */
  // Append a global search param (backend decides which fields to search)
  private addSearchParam(params: URLSearchParams, value: string): void {
    if (typeof value === 'string' && value.trim() !== '') {
      params.append('search', value.trim());
      if (this.options.searchFields) {
        params.append('search_fields', this.options.searchFields);
      }
    }
  }

  /**
   * Add date parameter with specified lookup
   */
  // Support date_gte/date_lte/date_exact/date_range; values serialized as YYYY-MM-DD
  private addDateParam(params: URLSearchParams, accessor: string, filter: FilterConfig, value: any): void {
    const lookup = filter.lookup || this.getLookupFromType(filter.type);

    if ((filter.type === 'date_range' || filter.type === 'date_time_range') && value && typeof value === 'object') {
      const bounds = resolveDateRangeBounds(value, filter.relativeDatePresets);
      if (bounds.start && this.isValidDate(bounds.start)) {
        params.append(`${accessor}__gte`, formatLocalYmd(bounds.start));
      }
      if (bounds.end && this.isValidDate(bounds.end)) {
        params.append(`${accessor}__lte`, formatLocalYmd(bounds.end));
      }
    } else if (this.isValidDate(value)) {
      const date = value instanceof Date ? value : new Date(value);
      const dateOnly = date.toISOString().slice(0, 10);
      if (filter.type === 'date_exact') {
        params.append(accessor, dateOnly);
        return;
      }
      const paramName = lookup === 'gte' || lookup === '' ? `${accessor}__gte` :
                       lookup === 'lte' ? `${accessor}__lte` :
                       lookup === 'gt' ? `${accessor}__gt` :
                       lookup === 'lt' ? `${accessor}__lt` : `${accessor}__${lookup}`;
      params.append(paramName, dateOnly);
    }
  }

  /**
   * Add number parameter with specified lookup
   */
  // Support numeric bounds (gte/lte/gt/lt)
  private addNumberParam(params: URLSearchParams, accessor: string, filter: FilterConfig, value: any): void {
    const lookup = filter.lookup || this.getLookupFromType(filter.type);

    if (filter.type === 'number_range' && value && typeof value === 'object') {
      const bounds = resolveNumberRangeBounds(value, filter.rangePresets);
      if (this.isValidNumber(bounds.min)) {
        params.append(`${accessor}__gte`, String(bounds.min));
      }
      if (this.isValidNumber(bounds.max)) {
        params.append(`${accessor}__lte`, String(bounds.max));
      }
      return;
    }

    if (this.isValidNumber(value)) {
      const paramName = lookup === 'gte' || lookup === '' ? `${accessor}__gte` :
                       lookup === 'lte' ? `${accessor}__lte` :
                       lookup === 'gt' ? `${accessor}__gt` :
                       lookup === 'lt' ? `${accessor}__lt` : `${accessor}__${lookup}`;
      params.append(paramName, String(value));
    }
  }

  /**
   * Add generic parameter with specified lookup
   */
  // Fallback handling for explicit lookups like exact, startswith, in, etc.
  private addGenericParam(params: URLSearchParams, accessor: string, filter: FilterConfig, value: any): void {
    const lookup = filter.lookup || this.getLookupFromType(filter.type);

    if (Array.isArray(value) && value.length > 0) {
      if (lookup === 'in' || lookup === '') {
        params.append(accessor, value.join(','));
      } else {
        value.forEach(v => {
          const paramName = lookup === 'in' || lookup === '' ? accessor : `${accessor}__${lookup}`;
          params.append(paramName, v);
        });
      }
    } else if (typeof value === 'string' && value.trim() !== '') {
      const paramName = lookup === 'icontains' || lookup === '' ? `${accessor}__icontains` : `${accessor}__${lookup}`;
      params.append(paramName, value.trim());
    } else if (this.isValidNumber(value)) {
      const paramName = lookup === 'icontains' || lookup === '' ? `${accessor}__icontains` : `${accessor}__${lookup}`;
      params.append(paramName, String(value));
    }
  }

  /**
   * Add default parameter (fallback)
   */
  // Final fallback for unknown types; sensible icontains/in behavior
  private addDefaultParam(params: URLSearchParams, accessor: string, value: any, lookup: string = 'icontains'): void {
    if (Array.isArray(value) && value.length > 0) {
      if (lookup === 'in' || lookup === '') {
        params.append(accessor, value.join(','));
      } else {
        value.forEach(v => params.append(`${accessor}__${lookup}`, v));
      }
    } else if (typeof value === 'string' && value.trim() !== '') {
      const paramName = lookup === 'icontains' || lookup === '' ? `${accessor}__icontains` : `${accessor}__${lookup}`;
      params.append(paramName, value.trim());
    } else if (this.isValidNumber(value)) {
      const paramName = lookup === 'icontains' || lookup === '' ? `${accessor}__icontains` : `${accessor}__${lookup}`;
      params.append(paramName, String(value));
    }
  }

  /**
   * Check if a value is empty
   */
  private isEmpty(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (value instanceof Date) return isNaN(value.getTime());
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') {
      const range = value as { start?: unknown; end?: unknown; min?: unknown; max?: unknown; preset?: unknown };
      if (range.preset) return false;
      if ('min' in range || 'max' in range) {
        return !this.isValidNumber(range.min) && !this.isValidNumber(range.max);
      }
      if ('start' in range || 'end' in range) {
        return !range.start && !range.end;
      }
      return Object.keys(value).length === 0;
    }
    return false;
  }

  /**
   * Check if a value is a valid date
   */
  private isValidDate(value: any): boolean {
    if (!value) return false;
    const date = value instanceof Date ? value : new Date(value);
    return !isNaN(date.getTime());
  }

  /**
   * Check if a value is a valid number
   */
  private isValidNumber(value: any): boolean {
    if (typeof value === 'number') return !isNaN(value);
    if (typeof value === 'string') return !isNaN(Number(value)) && value.trim() !== '';
    return false;
  }

  /**
   * Get a human-readable description of active filters
   */
  getFilterDescription(filterValues: Record<string, any>): string {
    const activeFilters: string[] = [];

    this.filters.forEach(filter => {
      const value = filterValues[filter.key];
      if (this.isEmpty(value)) return;

      const description = this.getFilterValueDescription(filter, value);
      if (description) {
        activeFilters.push(description);
      }
    });

    return activeFilters.length > 0 ? activeFilters.join(', ') : 'No filters applied';
  }

  /**
   * Get description for a single filter value
   */
  private getFilterValueDescription(filter: FilterConfig, value: any): string | null {
    switch (filter.type) {
      case 'select':
        if (Array.isArray(value)) {
          const labels = value.map(val => {
            const option = filter.options?.find(opt => opt.value === val);
            return option?.label || val;
          });
          return `${filter.label}: ${labels.join(', ')}`;
        } else {
          const option = filter.options?.find(opt => opt.value === value);
          return `${filter.label}: ${option?.label || value}`;
        }

      case 'text':
      case 'search':
        return `${filter.label}: "${value}"`;

      case 'date_gte': {
        const startDate = value instanceof Date ? value : new Date(value);
        return `${filter.label} from ${startDate.toLocaleDateString()}`;
      }

      case 'date_lte': {
        const endDate = value instanceof Date ? value : new Date(value);
        return `${filter.label} to ${endDate.toLocaleDateString()}`;
      }

      case 'date_exact': {
        const exactDate = value instanceof Date ? value : new Date(value);
        return `${filter.label}: ${exactDate.toLocaleDateString()}`;
      }

      case 'date_range':
        if (Array.isArray(value)) {
          const labels = value.map((id) => {
            const preset = filter.relativeDatePresets?.find((p) => p.id === id);
            return preset ? relativeDatePresetLabel(preset) : String(id);
          });
          return `${filter.label}: ${labels.join(', ')}`;
        }
        if (value && typeof value === 'object') {
          if (value.preset) {
            const preset = filter.relativeDatePresets?.find((p) => p.id === value.preset);
            return `${filter.label}: ${preset ? relativeDatePresetLabel(preset) : value.preset}`;
          }
          const start = value.start instanceof Date ? value.start.toLocaleDateString() : String(value.start || '');
          const end = value.end instanceof Date ? value.end.toLocaleDateString() : String(value.end || '');
          return `${filter.label}: ${start} - ${end}`;
        }
        return `${filter.label}: ${String(value)}`;

      case 'date_time_range':
        if (value && typeof value === 'object') {
          const start = value.start instanceof Date ? value.start.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : String(value.start || '');
          const end = value.end instanceof Date ? value.end.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : String(value.end || '');
          return `${filter.label}: ${start} - ${end}`;
        }
        return `${filter.label}: ${String(value)}`;

      case 'number_gte':
        return `${filter.label} ≥ ${value}`;

      case 'number_lte':
        return `${filter.label} ≤ ${value}`;

      case 'number_range':
        if (Array.isArray(value)) {
          const labels = value.map((id) => {
            const preset = filter.rangePresets?.find((p) => p.id === id);
            return preset ? numberRangePresetLabel(preset) : String(id);
          });
          return `${filter.label}: ${labels.join(', ')}`;
        }
        if (value && typeof value === 'object') {
          if (value.preset) {
            const preset = filter.rangePresets?.find((p) => p.id === value.preset);
            return `${filter.label}: ${preset ? numberRangePresetLabel(preset) : value.preset}`;
          }
          const min = value.min !== undefined && value.min !== '' ? String(value.min) : '';
          const max = value.max !== undefined && value.max !== '' ? String(value.max) : '';
          if (min && max) return `${filter.label}: ${min} – ${max}`;
          if (min) return `${filter.label} ≥ ${min}`;
          if (max) return `${filter.label} ≤ ${max}`;
        }
        return `${filter.label}: ${String(value)}`;

      default:
        return `${filter.label}: ${String(value)}`;
    }
  }

}

/** Restore filter UI values from bookmarkable query params. */
export function parseFilterValuesFromUrl(
  filters: FilterConfig[],
  urlParams: URLSearchParams
): Record<string, any> {
  const filterValues: Record<string, any> = {};

  filters.forEach((filter) => {
    const accessor = filter.accessor || filter.key;

    switch (filter.type) {
      case 'select':
      case 'in': {
        const allValues = urlParams.getAll(accessor);
        if (allValues.length > 1) {
          filterValues[filter.key] = allValues;
        } else if (allValues.length === 1 && allValues[0]) {
          filterValues[filter.key] = allValues[0].includes(',')
            ? allValues[0].split(',').filter(Boolean)
            : allValues;
        }
        break;
      }
      case 'date_range':
      case 'date_time_range': {
        const startValue = urlParams.get(`${accessor}__gte`);
        const endValue = urlParams.get(`${accessor}__lte`);
        if (startValue || endValue) {
          const matched = matchRelativeDatePresetFromParams(
            filter.relativeDatePresets,
            startValue,
            endValue
          );
          filterValues[filter.key] = matched
            ? [matched.id]
            : {
                start: startValue ? new Date(startValue) : undefined,
                end: endValue ? new Date(endValue) : undefined,
              };
        }
        break;
      }
      case 'number_range': {
        const minValue = urlParams.get(`${accessor}__gte`);
        const maxValue = urlParams.get(`${accessor}__lte`);
        if (minValue || maxValue) {
          const matched = matchNumberPresetFromParams(filter.rangePresets, minValue, maxValue);
          filterValues[filter.key] = matched
            ? [matched.id]
            : {
                min: minValue ?? '',
                max: maxValue ?? '',
              };
        }
        break;
      }
      case 'date_gte':
      case 'number_gte': {
        const paramValue = urlParams.get(`${accessor}__gte`);
        if (paramValue !== null) filterValues[filter.key] = paramValue;
        break;
      }
      case 'date_lte':
      case 'number_lte': {
        const paramValue = urlParams.get(`${accessor}__lte`);
        if (paramValue !== null) filterValues[filter.key] = paramValue;
        break;
      }
      case 'gt': {
        const paramValue = urlParams.get(`${accessor}__gt`);
        if (paramValue !== null) filterValues[filter.key] = paramValue;
        break;
      }
      case 'lt': {
        const paramValue = urlParams.get(`${accessor}__lt`);
        if (paramValue !== null) filterValues[filter.key] = paramValue;
        break;
      }
      case 'search': {
        const paramValue = urlParams.get('search');
        if (paramValue !== null) filterValues[filter.key] = paramValue;
        break;
      }
      case 'text':
      case 'icontains': {
        const paramValue = urlParams.get(`${accessor}__icontains`) ?? urlParams.get(accessor);
        if (paramValue !== null) filterValues[filter.key] = paramValue;
        break;
      }
      default: {
        const paramValue = urlParams.get(accessor);
        if (paramValue !== null) filterValues[filter.key] = paramValue;
        break;
      }
    }
  });

  return filterValues;
}